import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CampaignCreationQueryDataType, ConfigureMailDataType, EmailGeneratedStateProps, ErrorMessages, GetFolderDataType, IContentType, IContentTypes, IResetDetails, MasterDataType, MediaListType, MediaUploadType, PreviewProps, ProfanityDetailsType, TabStatesProps, TContentType, UserDataType } from '../../types/dataTypes';
import { ISalaryRange } from '@foundit/salaryRange/dist/types/salaryRange';
import { IInventoryUsageData } from '../../types/campaign.types';
import { MAX_COUNT } from '../../utils/whatsAppUtils';

interface CampaignSliceSlice {
  campaignId: string | null,
  campaignCreationQueryData: CampaignCreationQueryDataType,
  folderName: string;
  folderId: string;
  candidateNo: string;
  sendMailToContactedProfiles: boolean;
  companyName: string;
  originalCompanyName: string;
  aboutCompany: string;
  senderId: string;
  replyId: string;
  jobTitle: string;
  jobLocation: string[];
  minExperience: string;
  maxExperience: string;
  minSalary: string;
  maxSalary: string;
  campaignTemplate: string;
  campaignName: string;
  errorMessages: ErrorMessages;
  tabState: TabStatesProps[];
  selectedTab: string;
  userData: UserDataType;
  userDetails: any;
  isTemplateForCampaignUsed: boolean,
  isMediaFetched: boolean,
  contentTypes : IContentTypes,
  contentPersonalisationType: string,
  contentPersonalisationGenratedViaAI: 0 | 1,
  JDGeneratedByAi: boolean,
  selectedContentPersonalisationKey: number
  mediaList: MediaListType,
  signature: string,
  isMediaUploadAllowed: MediaUploadType,
  emailGeneratedState: EmailGeneratedStateProps,
  previewState: PreviewProps[],
  previewNo: number,
  isBlastDisabled: boolean
  companyReplyId: string,
  currency: string,
  templateSelected: boolean
  jobDescription: string,
  isCampaignTemplateClicked: boolean,
  jobMode: string,
  passiveJobs: boolean,
  outreachType: string,
  isMaxBlastLimitReached: boolean,
  nonContactedCandidateCount: string,
  selectedContactType: string,
  allFolders: GetFolderDataType[],
  showPassiveJobs: boolean,
  isBridgeCustomer: boolean,
  resetDetails : IResetDetails,
  disabledFields: {
    minExp: boolean;
    maxExp: boolean;
    jobLocations: boolean;
  };
  masterData: MasterDataType,
  mailStripData?: ConfigureMailDataType,
  bannerData: {
    message:JSX.Element |string,
    cta:string
  }[],
  inventoryUsage?: IInventoryUsageData
}

const initialState: CampaignSliceSlice = {
  bannerData:[],
  campaignId: null,
  campaignCreationQueryData: {
    campaignInitiationId: '',
    triggeredFromPage: '',
    timeZone: '',
    isBulkCampaign: 'false',
    searchId: '',
    childSearchId: ''
  },
  folderName: '',
  folderId: '',
  candidateNo: '0',
  sendMailToContactedProfiles: true,
  companyName: '',
  originalCompanyName: "",
  aboutCompany: '',
  senderId: 'opportunities@foundit.in',
  replyId: '',
  errorMessages: {
    folderName: '',
    replyId: '',
    minExp: '',
    maxExp: '',
    campaignName: '',
    jobTitle: '',
    candidateInputNumber: '',
    salary: '',
    jobLocation: ''
  },
  jobTitle: '',
  jobLocation: [],
  minExperience: '',
  maxExperience: '',
  minSalary: '',
  maxSalary: '',
  campaignTemplate: '',
  campaignName: '',
  tabState: [{
    label: 'Email',
    subject: '',
    email: '',
    applyButton: true,
    replyButton: true,
    stepNumber: 1,
    selectedFollowUPdays: '0',
    showError: false,
    profanityWords: [],
    profanityWordsInSubject: [],
    alreadyRephrased: false,
    alreadyGenerated: false,
    candidateFullName: ""
  }],
  previewState: [],
  previewNo: 1,
  emailGeneratedState: {
    rephraseCount: 0,
    currentCount: 0,
    viewed: [],
    rephrasedEmailObj: [],
  },
  selectedTab: 'Email',
  userData: {
    recruiterId: '',
    channelId: '',
    corpId: '',
    subChannelId: '',
    username: '',
    trackingId: '',
    siteContext: '',
    campaignMaxLimit: `${MAX_COUNT}`
  },
  userDetails: null,
  isTemplateForCampaignUsed: false,
  contentPersonalisationType: '',
  contentPersonalisationGenratedViaAI: 0,
  JDGeneratedByAi: false,
  selectedContentPersonalisationKey: 0,
  mediaList: {
    cover: '',
    logo: '',
  },
  isMediaFetched: false,
  contentTypes:{},
  signature: '',
  isMediaUploadAllowed: {
    cover: false,
    logo: false,
  },
  isBlastDisabled: false,
  companyReplyId: '',
  currency: '',
  templateSelected: false,
  jobDescription: '',
  isCampaignTemplateClicked: false,
  jobMode: 'WFO',
  passiveJobs: true,
  outreachType: 'SCHEDULED',
  isMaxBlastLimitReached: false,
  nonContactedCandidateCount: '',
  selectedContactType: 'All',
  allFolders: [],
  showPassiveJobs: false,
  isBridgeCustomer: false,
  resetDetails: {
    resetCount: 0,
    originalContent:null,
    newContent: null
  },
disabledFields: {
    minExp: false,
    maxExp: false,
    jobLocations: false,
  },
  masterData:{
    channelId:null,
    subChannelId:null,
    recruiterId: null
  }
};

const campaignSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    setIsBridgeCustomerState: (state, action) => {
      state.isBridgeCustomer = action.payload.isBridgeCustomer;
    },
    resetFormExceptQueryData: (state) => ({
      ...initialState,
      campaignCreationQueryData: state.campaignCreationQueryData,
      userData: state.userData,
    }),
    setFolderName: (state, action) => {
      state.folderName = action.payload.folderName;
    },
    setFolderId: (state, action) => {
      state.folderId = action.payload.folderId;
    },
    setMailStripDataRedux: (state, action) => {
      state.mailStripData = action.payload.mailStripData;
    },
    setResetDetails: (state,action:PayloadAction<{ newContent?: TContentType, isResetClicked?: boolean, originalContent?: TContentType }>) => {
      const {isResetClicked, newContent, originalContent} = action.payload;

      state.resetDetails = {
        resetCount: isResetClicked ? state.resetDetails.resetCount + 1 : state.resetDetails.resetCount,
        newContent: newContent || state.resetDetails.newContent,
        originalContent: originalContent || state.resetDetails.originalContent,
      }

    },
    setCandidateNo: (state, action) => {
      state.candidateNo = action.payload.candidateNo;
    },
    setCompanyName: (state, action) => {
      state.companyName = action.payload.companyName;
    },
    setBannerData: (state, action:PayloadAction<{bannerData: {message:string|JSX.Element, cta:string}[]}>) => {
      state.bannerData = action.payload.bannerData;
    },
    setInventoryUsage: (state, action:PayloadAction<{inventoryUsage: IInventoryUsageData}>) => {
      state.inventoryUsage = action.payload.inventoryUsage;
    },
    setOriginalCompanyName: (state, action) => {
      state.originalCompanyName = action.payload.companyName;
    },
    setAboutCompany: (state, action) => {
      state.aboutCompany = action.payload.aboutCompany;
    },
    setSenderId: (state, action) => {
      state.senderId = action.payload.senderId;
    },
    setReplyId: (state, action) => {
      state.replyId = action.payload.replyId;
    },
    setErrorMessages: (state, action) => {
      state.errorMessages = {
        ...state.errorMessages,
        ...action.payload.errorMessages,
      };
    },
    setJobTitle: (state, action) => {
      state.jobTitle = action.payload.jobTitle;
    },
    setJobLocation: (state, action) => {
      state.jobLocation = action.payload.jobLocation;
    },
    setMinExperience: (state, action) => {
      state.minExperience = action.payload.minExperience;
    },
    setMaxExperience: (state, action) => {
      state.maxExperience = action.payload.maxExperience;
    },
    setMinSalary: (state, action) => {
      state.minSalary = action.payload.minSalary;
    },
    setMaxSalary: (state, action) => {
      state.maxSalary = action.payload.maxSalary;
    },
    setSalaryRange(
      state,
      action: PayloadAction<{ salaryRange: ISalaryRange }>
  ) {
      const { currency, min ,max} = action.payload.salaryRange
        state.currency = currency;
        state.minSalary = min;
        state.maxSalary = max;
    },
    setIsMediaFetched(state,  action: PayloadAction<{ isMediaFetched: boolean }>){
      state.isMediaFetched = action.payload.isMediaFetched;
    },
    setContentTypes(state,  action: PayloadAction<{ tabIndex:number, contentType: IContentType}>){
      const {tabIndex, contentType} = action.payload;
      state.contentTypes[tabIndex] = contentType;
    },
    setCampaignTemplate: (state, action) => {
      state.campaignTemplate = action.payload.campaignTemplate;
    },
    setCampaignName: (state, action) => {
      state.campaignName = action.payload.campaignName;
    },
    setTabStates: (state, action) => {
      state.tabState = action.payload.tabState;
    },
    setUserData: (state, action) => {
      state.userData = action.payload.userData;
    },
    setUserDetails: (state, action) => {
      state.userDetails = action.payload.userDetails;
    },
    setTempalteForCampaign: (state, action) => {
      state.isTemplateForCampaignUsed = action.payload.isTemplateForCampaignUsed
    },
    setCampaignId: (state, action) => {
      state.campaignId = action.payload.campaignId
    },
    setCampaignCreationQueryData: (state, action) => {
      state.campaignCreationQueryData = action.payload.campaignCreationQueryData
    },
    setSendMailToContactedProfiles: (state, action) => {
      state.sendMailToContactedProfiles = action.payload.sendMailToContactedProfiles
    },
    setActiveTab: (state, action) => {
      state.selectedTab = action.payload.selectedTab
    },
    setContentPersonalisationType: (state, action) => {
      state.contentPersonalisationType = action.payload.contentPersonalisationType
    },
    setContentPersonalisationGenratedViaAI: (state, action) => {
      state.contentPersonalisationGenratedViaAI = action.payload.contentPersonalisationGenratedViaAI
    },
    setJDGeneratedByAi: (state, action) => {
      state.JDGeneratedByAi = action.payload.JDGeneratedByAi
    },
    setSelectedContentPersonalisationKey: (state, action) => {
      state.selectedContentPersonalisationKey = action.payload.selectedContentPersonalisationKey
    },
    setMedia: (state, action) => {
      state.mediaList = {
        ...state.mediaList,
        ...action.payload.mediaList,
      };
    },
    setSignature: (state, action) => {
      state.signature = action.payload.signature
    },
    setIsMediaUploadAllowed: (state, action) => {
      state.isMediaUploadAllowed = action.payload.isMediaUploadAllowed
    },
    setCompleteEmailReset: (state) => {
      state.tabState = initialState.tabState;
      state.selectedTab = initialState.selectedTab;
      state.emailGeneratedState = initialState.emailGeneratedState;
      state.contentPersonalisationType = initialState.contentPersonalisationType;
      state.selectedContentPersonalisationKey = initialState.selectedContentPersonalisationKey
      state.previewState = initialState.previewState
      state.previewNo = initialState.previewNo;
      state.jobDescription = initialState.jobDescription;
      state.contentPersonalisationGenratedViaAI = initialState.contentPersonalisationGenratedViaAI
      state.templateSelected = initialState.templateSelected;
      state.isTemplateForCampaignUsed = initialState.isTemplateForCampaignUsed;
      state.campaignTemplate = initialState.campaignTemplate;
      state.jobDescription = initialState.jobDescription;
      state.JDGeneratedByAi = initialState.JDGeneratedByAi;
    },
    resetEmailData: (state) => {
          state.tabState = [...state.tabState].map(i => {
            if (i.label == state.selectedTab) {
              return ({
                ...i,
                subject: '',
                email: '',
                applyButton: true,
                replyButton: true,
                showError: false,
                profanityWords: [],
                profanityWordsInSubject: [],
                alreadyRephrased: false,
                alreadyGenerated: false
              })
            } else {
              return i
            }
    
          });
          if (state.selectedTab == 'Email') {
            state.emailGeneratedState = initialState.emailGeneratedState;
            state.contentPersonalisationType = initialState.contentPersonalisationType;
            state.selectedContentPersonalisationKey = initialState.selectedContentPersonalisationKey
            state.previewState = initialState.previewState
            state.previewNo = initialState.previewNo;
            state.jobDescription = initialState.jobDescription;
          }
          if (!state.tabState?.some(i => (i?.label !== state.selectedTab && (i?.alreadyRephrased || i?.alreadyGenerated)))) {
            state.contentPersonalisationGenratedViaAI = initialState.contentPersonalisationGenratedViaAI
          }
      state.templateSelected = initialState.templateSelected;
      state.isTemplateForCampaignUsed = initialState.isTemplateForCampaignUsed;
      state.campaignTemplate = initialState.campaignTemplate;
    },
    resetGeneratedAiButton: (state) => {
      state.emailGeneratedState = initialState.emailGeneratedState;
    },
    setRephraseCount: (state, action) => {
      state.emailGeneratedState = { ...state.emailGeneratedState, rephraseCount: action.payload.rephraseCount }
    },
    setCurrentCount: (state, action) => {
      state.emailGeneratedState = { ...state.emailGeneratedState, currentCount: action.payload.currentCount }
    },
    setViewed: (state, action) => {
      state.emailGeneratedState = { ...state.emailGeneratedState, viewed: action.payload.viewed }
    },
    setRephrasedEmailObj: (state, action) => {
      state.emailGeneratedState = { ...state.emailGeneratedState, rephrasedEmailObj: action.payload.rephrasedEmailObj }
    },
    setPreviews: (state, action) => {
      state.previewState = action.payload.previewState
    },
    setPreviewNo: (state, action) => {
      state.previewNo = action.payload
    },
    setDisableBlast: (state, action) => {
      state.isBlastDisabled = action.payload
    },
    setCompanyReplyId: (state, action) => {
      state.companyReplyId = action.payload.companyReplyId
    },
    setCurrency: (state, action) => {
      state.currency = action.payload.currency || ""
    },
    setTemplateSelected: (state, action) => {
      state.templateSelected = action.payload.templateSelected
    },
    setJobDescription: (state, action) => {
      state.jobDescription = action.payload.jobDescription
    },
    setIsCampaignTemplateClicked: (state, action) => {
      state.isCampaignTemplateClicked = action.payload.isCampaignTemplateClicked
    },
    setJobMode: (state, action) => {
      state.jobMode = action.payload.jobMode
    },
    setPassiveJobs: (state, action) => {
      state.passiveJobs = action.payload
    },
    setOutreachType: (state, action) => {
      state.outreachType = action.payload.outreachType
    },
    setIsMaxBlastLimitReached: (state,action) => {
      state.isMaxBlastLimitReached = action.payload.isMaxBlastLimitReached
    },
    setNonContactedCandidateCount: (state, action) => { 
      state.nonContactedCandidateCount = action.payload.nonContactedCandidateCount
    },
    setSelectedContactDetailsType: (state, action) => {
      state.selectedContactType = action.payload.selectedContactType
    },
    setAllFolders: (state, action) => {
      state.allFolders = action.payload.allFolders    
    },
    setShowPassiveJobs: (state,action) => {
      state.showPassiveJobs = action.payload.showPassiveJobs
    },
     setDisabledField: (
      state,
      action: PayloadAction<{
        minExp: number | null | string | boolean;
        maxExp: number | null | string | boolean;
        jobLocations: string[] | null | boolean;
      }>
    ) => {
      // state.disabledFields[action.payload.field] = action.payload.value;
       const { minExp, maxExp, jobLocations } = action.payload;

      if (typeof minExp === "boolean") {
      state.disabledFields.minExp = minExp;
      } else {
        state.disabledFields.minExp =
          minExp !== null && minExp !== undefined && minExp !== "";
      }

      if (typeof maxExp === "boolean") {
        state.disabledFields.maxExp = maxExp;
      } else {
        state.disabledFields.maxExp =
          maxExp !== null && maxExp !== undefined && maxExp !== "";
      }
 
      if (typeof jobLocations === "boolean") {
        state.disabledFields.jobLocations = jobLocations;
      } else if (Array.isArray(jobLocations)) {
        state.disabledFields.jobLocations = jobLocations.length > 0;
      } else {
        state.disabledFields.jobLocations = false;
      }
    },
    setMasterData:(state,action) => {
      state.masterData = action.payload.masterData
    },
  },
});

export const {
  resetFormExceptQueryData, setFolderName, setCandidateNo, setCompanyName,setOriginalCompanyName, setAboutCompany, setSenderId,
  setReplyId, setErrorMessages, setJobTitle, setJobLocation, setMaxExperience,setUserDetails, setMailStripDataRedux,
  setMinExperience, setMaxSalary, setMinSalary, setCampaignName, setCampaignTemplate, setTabStates,
  setFolderId, setUserData, setTempalteForCampaign, setCampaignId, setCampaignCreationQueryData, setSendMailToContactedProfiles,
  setActiveTab, setContentPersonalisationType, setContentPersonalisationGenratedViaAI, setJDGeneratedByAi, setSelectedContentPersonalisationKey, setMedia, setSignature, setIsMediaUploadAllowed,
  resetEmailData, setRephraseCount, setCurrentCount, setViewed, setRephrasedEmailObj, resetGeneratedAiButton, setPreviews,  setPreviewNo, setCompanyReplyId, setDisableBlast, setCurrency, setTemplateSelected, setJobDescription,
  setIsCampaignTemplateClicked, setJobMode, setNonContactedCandidateCount, setSelectedContactDetailsType, setPassiveJobs, setOutreachType, setIsMaxBlastLimitReached, setCompleteEmailReset, setAllFolders, setShowPassiveJobs, setIsBridgeCustomerState,
  setResetDetails, setSalaryRange, setIsMediaFetched, setContentTypes, setDisabledField, setMasterData, setBannerData,setInventoryUsage
} = campaignSlice.actions;
export default campaignSlice.reducer;

// 406
